import { useState } from "react";
import { KeyRound, Loader2, Check } from "lucide-react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "@/components/ui/dialog";
import { api } from "./api";

// FastAPI 422 returns an array of {msg} objects — never render `detail` raw.
const formatDetail = (detail) => {
    if (detail == null) return "Could not change the password. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
    if (typeof detail?.msg === "string") return detail.msg;
    return String(detail);
};

const EMPTY = { current: "", next: "", confirm: "" };

export const ChangePasswordDialog = ({ open, onOpenChange }) => {
    const [form, setForm] = useState(EMPTY);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState(false);
    const [saving, setSaving] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const close = () => {
        setForm(EMPTY);
        setError("");
        setSuccess(false);
        onOpenChange(false);
    };

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (form.next.length < 8) {
            setError("New password must be at least 8 characters.");
            return;
        }
        if (form.next !== form.confirm) {
            setError("New passwords do not match.");
            return;
        }
        setSaving(true);
        try {
            await api.post("/auth/change-password", {
                current_password: form.current,
                new_password: form.next,
            });
            setSuccess(true);
            setForm(EMPTY);
        } catch (err) {
            setError(formatDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const fieldCls =
        "w-full rounded-lg border border-[#1D322E] bg-[#0E1A17] px-3 py-2 text-sm text-[#F7F5EF] placeholder-[#64716D] outline-none focus:border-[#10B981] transition-colors";

    return (
        <Dialog open={open} onOpenChange={(v) => (v ? onOpenChange(true) : close())}>
            <DialogContent
                className="max-w-md bg-[#12201D] border-[#1D322E] text-[#F7F5EF] p-6"
                data-testid="change-password-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-lg text-[#F7F5EF]">
                        <KeyRound className="w-4 h-4 text-emerald-400" />
                        Change your password
                    </DialogTitle>
                    <DialogDescription className="text-sm text-[#A3B8B0]">
                        Choose a strong password you don't use anywhere else.
                    </DialogDescription>
                </DialogHeader>

                {success ? (
                    <div data-testid="change-password-success" className="py-4 text-center">
                        <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-950/60 border border-emerald-800/70">
                            <Check className="w-5 h-5 text-emerald-300" />
                        </span>
                        <p className="mt-4 text-sm font-semibold text-[#F7F5EF]">Password changed</p>
                        <p className="mt-1 text-xs text-[#A3B8B0]">
                            Your session was refreshed — you stay signed in.
                        </p>
                        <button
                            type="button"
                            data-testid="change-password-done-button"
                            onClick={close}
                            className="mt-6 w-full rounded-lg bg-[#10B981] px-4 py-2.5 text-sm font-bold text-[#0B1513] transition-all hover:bg-emerald-400 active:scale-[0.99]"
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="mt-2 space-y-4" data-testid="change-password-form">
                        <div>
                            <label htmlFor="cp-current" className="text-xs font-semibold uppercase tracking-wider text-[#A3B8B0]">
                                Current password
                            </label>
                            <input
                                id="cp-current"
                                data-testid="change-password-current-input"
                                type="password"
                                autoComplete="current-password"
                                required
                                value={form.current}
                                onChange={set("current")}
                                className={`mt-1.5 ${fieldCls}`}
                            />
                        </div>
                        <div>
                            <label htmlFor="cp-new" className="text-xs font-semibold uppercase tracking-wider text-[#A3B8B0]">
                                New password
                            </label>
                            <input
                                id="cp-new"
                                data-testid="change-password-new-input"
                                type="password"
                                autoComplete="new-password"
                                required
                                minLength={8}
                                value={form.next}
                                onChange={set("next")}
                                placeholder="At least 8 characters"
                                className={`mt-1.5 ${fieldCls}`}
                            />
                        </div>
                        <div>
                            <label htmlFor="cp-confirm" className="text-xs font-semibold uppercase tracking-wider text-[#A3B8B0]">
                                Confirm new password
                            </label>
                            <input
                                id="cp-confirm"
                                data-testid="change-password-confirm-input"
                                type="password"
                                autoComplete="new-password"
                                required
                                value={form.confirm}
                                onChange={set("confirm")}
                                className={`mt-1.5 ${fieldCls}`}
                            />
                        </div>

                        {error && (
                            <p
                                role="alert"
                                data-testid="change-password-error"
                                className="rounded-lg border border-red-900/60 bg-red-950/40 px-3 py-2 text-xs text-red-300"
                            >
                                {error}
                            </p>
                        )}

                        <div className="flex gap-3 pt-1">
                            <button
                                type="button"
                                data-testid="change-password-cancel-button"
                                onClick={close}
                                className="flex-1 rounded-lg border border-[#1D322E] px-4 py-2.5 text-sm font-semibold text-[#A3B8B0] hover:text-[#F7F5EF] hover:border-[#0B5148] transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                data-testid="change-password-submit-button"
                                disabled={saving}
                                className="flex-1 inline-flex items-center justify-center gap-2 rounded-lg bg-[#10B981] px-4 py-2.5 text-sm font-bold text-[#0B1513] transition-all hover:bg-emerald-400 active:scale-[0.99] disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                {saving ? "Changing…" : "Change password"}
                            </button>
                        </div>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
};
